import { Component, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	SetupWizardSectionFormPayload,
	SECTION_CSV_LABELS,
	SectionCsvKeys,
	SectionCsvTemplate,
	SECTION_CSV_SPEC,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { TextField } from '../../../fields/setup-wizard/text-field/text-field';
import { CheckboxField } from '../../../fields/setup-wizard/checkbox-field/checkbox-field';
import { LanguageDropdownField } from '../../../fields/setup-wizard/language-dropdown-field/language-dropdown-field';
import { IntegerField } from '../../../fields/setup-wizard/integer-field/integer-field';
import { DatePickerField } from '../../../fields/setup-wizard/date-picker-field/date-picker-field';
import { FormUtil } from '../../../../utils/form.util';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

type SectionForm = {
	[SectionCsvKeys.ssoSectionId]: FormControl<string | null>;
	[SectionCsvKeys.ssoCourseId]: FormControl<string | null>;
	[SectionCsvKeys.name]: FormControl<string | null>;
	[SectionCsvKeys.language]: FormControl<string | null>;
	[SectionCsvKeys.displayOrder]: FormControl<number | null>;
	[SectionCsvKeys.activeStartDate]: FormControl<Date | null>;
	[SectionCsvKeys.activeEndDate]: FormControl<Date | null>;
	[SectionCsvKeys.sectionCode]: FormControl<string | null>;
	[SectionCsvKeys.allowSelfRostering]: FormControl<boolean | null>;
	[SectionCsvKeys.sectionUuid]: FormControl<string | null>;
	[SectionCsvKeys.courseUuid]: FormControl<string | null>;
	[SectionCsvKeys.active]: FormControl<boolean | null>;
};

@Component({
	selector: 'app-setup-wizard-section-form',
	imports: [
		ReactiveFormsModule,
		NzButtonModule,
		NzFormModule,
		NzInputModule,
		NzSpinModule,
		TextField,
		CheckboxField,
		LanguageDropdownField,
		IntegerField,
		DatePickerField,
	],
	standalone: true,
	templateUrl: './setup-wizard-section-form.html',
	styleUrl: './setup-wizard-section-form.scss',
})
export class SetupWizardSectionForm {
	public courseUuid = input.required<string>();
	public edit = input<SectionCsvTemplate | null>(null);
	public loading = input<boolean>(false);
	public onClear = output<void>();
	public onSubmit = output<SetupWizardSectionFormPayload>();
	public SectionCsvKeys = SectionCsvKeys;
	public SECTION_CSV_LABELS = SECTION_CSV_LABELS;
	public Validators = Validators;
	private clientUuid: string | undefined;
	private sub?: Subscription;

	public form = new FormGroup<SectionForm>({
		[SectionCsvKeys.ssoSectionId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[SectionCsvKeys.ssoCourseId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[SectionCsvKeys.name]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[SectionCsvKeys.language]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[SectionCsvKeys.displayOrder]: new FormControl<number | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[SectionCsvKeys.activeStartDate]: new FormControl<Date | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[SectionCsvKeys.activeEndDate]: new FormControl<Date | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[SectionCsvKeys.sectionCode]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[SectionCsvKeys.allowSelfRostering]: new FormControl<boolean | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[SectionCsvKeys.sectionUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[SectionCsvKeys.courseUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[SectionCsvKeys.active]: new FormControl<boolean | null>(null, {
			nonNullable: false,
			validators: [],
		}),
	});

	constructor(private route: ActivatedRoute) {
		FormUtil.applyCsvSpecValidators(this.form, SECTION_CSV_SPEC);

		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	ngOnChanges() {
		const edit = this.edit();

		if (edit) {
			// If edit contains a sectionUuid we can remove all validators for ssoSectionId
			if (edit[SectionCsvKeys.sectionUuid]) {
				this.form.get(SectionCsvKeys.ssoSectionId)?.clearValidators();
				this.form.get(SectionCsvKeys.ssoSectionId)?.updateValueAndValidity();
			}

			let activeStartDate: Date | null = null;
			let activeEndDate: Date | null = null;

			const editActiveStartDate: string | undefined = edit[SectionCsvKeys.activeStartDate] as string | undefined;
			const editActiveEndDate: string | undefined = edit[SectionCsvKeys.activeEndDate] as string | undefined;

			if (
				typeof editActiveStartDate === 'string' &&
				editActiveStartDate.trim() !== '' &&
				editActiveStartDate.length > 0
			) {
				activeStartDate = new Date(editActiveStartDate);
			} else {
				activeStartDate = null;
			}

			if (
				typeof editActiveEndDate === 'string' &&
				editActiveEndDate.trim() !== '' &&
				editActiveEndDate.length > 0
			) {
				activeEndDate = new Date(editActiveEndDate);
			} else {
				activeEndDate = null;
			}

			this.form.patchValue({
				[SectionCsvKeys.ssoSectionId]: edit[SectionCsvKeys.ssoSectionId],
				[SectionCsvKeys.ssoCourseId]: edit[SectionCsvKeys.ssoCourseId],
				[SectionCsvKeys.name]: edit[SectionCsvKeys.name],
				[SectionCsvKeys.language]: edit[SectionCsvKeys.language],
				[SectionCsvKeys.displayOrder]: edit[SectionCsvKeys.displayOrder],
				[SectionCsvKeys.activeStartDate]: activeStartDate,
				[SectionCsvKeys.activeEndDate]: activeEndDate,
				[SectionCsvKeys.sectionCode]: edit[SectionCsvKeys.sectionCode],
				[SectionCsvKeys.allowSelfRostering]: edit[SectionCsvKeys.allowSelfRostering],
				[SectionCsvKeys.sectionUuid]: edit[SectionCsvKeys.sectionUuid],
				[SectionCsvKeys.courseUuid]: edit[SectionCsvKeys.courseUuid],
				[SectionCsvKeys.active]: edit[SectionCsvKeys.active],
			});
		} else {
			const courseUuid = this.courseUuid();

			if (!courseUuid) {
				throw new Error('Course UUID is required');
			}

			this.form.patchValue({
				[SectionCsvKeys.courseUuid]: this.courseUuid(),
			});
		}
	}

	onFormSubmit() {
		if (this.form.valid) {
			const value = this.form.getRawValue();

			this.onSubmit.emit({
				[SectionCsvKeys.ssoSectionId]: value[SectionCsvKeys.ssoSectionId] ?? undefined,
				[SectionCsvKeys.ssoCourseId]: value[SectionCsvKeys.ssoCourseId] ?? undefined,
				[SectionCsvKeys.name]: value[SectionCsvKeys.name] ?? undefined,
				[SectionCsvKeys.language]: value[SectionCsvKeys.language] ?? undefined,
				[SectionCsvKeys.displayOrder]: value[SectionCsvKeys.displayOrder] ?? undefined,
				[SectionCsvKeys.activeStartDate]: value[SectionCsvKeys.activeStartDate]?.toString() ?? undefined,
				[SectionCsvKeys.activeEndDate]: value[SectionCsvKeys.activeEndDate]?.toString() ?? undefined,
				[SectionCsvKeys.sectionCode]: value[SectionCsvKeys.sectionCode] ?? undefined,
				[SectionCsvKeys.allowSelfRostering]: value[SectionCsvKeys.allowSelfRostering] ?? undefined,
				[SectionCsvKeys.sectionUuid]: value[SectionCsvKeys.sectionUuid] ?? undefined,
				[SectionCsvKeys.courseUuid]: value[SectionCsvKeys.courseUuid] ?? undefined,
				[SectionCsvKeys.active]: value[SectionCsvKeys.active] ?? undefined,
			});

			this.form.reset();
		} else {
			this.form.markAllAsTouched();
		}
	}

	onClearClicked() {
		this.onClear.emit();
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
